import React from 'react'
import { connect } from 'react-redux'
import DashboardActionBar from '../../DashboardActionBar'
import Loading from '../../Loading'
import TableOverlay from '../../TableOverlay'
import { getStarsJSX } from '../../redux/FeedbackRedux'
import { notifyError } from '../../ToastMessages'
import SmartTableGrid from '../../SmartTableGrid'
import ReactTooltip from 'react-tooltip'
import DateFormatter from '../../Classes/DateFormatter'

import BusinessNoteReminderModal from '../../business/BusinessNoteReminderModal'

function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

class AllDashboard extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            apiStatistics: [],
            apiDataTotals: [],
            statsLoading: true,
            totalsLoading: true,
            statsError: null,
            totalsError: null,
            timeframe: this.props.state.timeframe,
            showNoteReminderModal: false,
            selectedBusinessRow: null,
            onlyManagedServices: window.localStorage.getItem("only_managed_services") === "1",
        };

        this.handleNotesClick = this.handleNotesClick.bind(this);
    }

    handleNotesClick = (row) => {
        this.setState({
            showNoteReminderModal: true,
            selectedBusinessRow: row,
        });
    };

    fetchStatisticsData = (timeframe, dateFrom = null, dateTo = null) => {
        this.setState({ statsLoading: true, statsError: null });

        const params = new URLSearchParams({
            timeframe: timeframe || ""
        });

        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        fetch(`/feedback-dashboard/statistics?${params.toString()}`, {
            method: "GET",
            headers: {
                "X-Requested-With": "XMLHttpRequest"
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(__("Failed to load statistics. Please try again."));
                }
                return response.json();
            })
            .then(data => {
                if (this._isMounted) {
                    this.setState({ apiStatistics: data, statsLoading: false });
                }
            })
            .catch(err => {
                if (this._isMounted) {
                    this.setState({ statsLoading: false, statsError: err.message });
                    notifyError(err.message);
                }
            });
    };

    fetchTotalsData = (timeframe, dateFrom = null, dateTo = null) => {
        this.setState({ totalsLoading: true, totalsError: null });

        const params = new URLSearchParams({
            timeframe: timeframe || ""
        });

        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        fetch(`/feedback-dashboard/totals?${params.toString()}`, {
            method: "GET",
            headers: {
                "X-Requested-With": "XMLHttpRequest"
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(__("Failed to load totals. Please try again."));
                }
                return response.json();
            })
            .then(data => {
                if (this._isMounted) {
                    this.setState({ apiDataTotals: data, totalsLoading: false });
                }
            })
            .catch(err => {
                if (this._isMounted) {
                    this.setState({ totalsLoading: false, totalsError: err.message });
                    notifyError(err.message);
                }
            });
    };

    componentDidMount() {
        this._isMounted = true;
        const { timeframe } = this.props.state;

        if (timeframe === "custom") {
            const dateFrom = window.localStorage.getItem("prev_date");
            const dateTo = window.localStorage.getItem("next_date");
            this.fetchStatisticsData(timeframe, dateFrom, dateTo);
            this.fetchTotalsData(timeframe, dateFrom, dateTo);
        } else {
            this.fetchStatisticsData(timeframe);
            this.fetchTotalsData(timeframe);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleTimeframeChange = (newTimeframe) => {
        this.setState({ timeframe: newTimeframe });
        const dateFrom = window.localStorage.getItem("prev_date");
        const dateTo = window.localStorage.getItem("next_date");

        if (newTimeframe === "custom") {
            this.fetchStatisticsData(newTimeframe, dateFrom, dateTo);
            this.fetchTotalsData(newTimeframe, dateFrom, dateTo);
        } else {
            this.fetchStatisticsData(newTimeframe);
            this.fetchTotalsData(newTimeframe);
        }
    };

    retryFetch = () => {
        const { timeframe } = this.state;
        if (timeframe === "custom") {
            const dateFrom = window.localStorage.getItem("prev_date");
            const dateTo = window.localStorage.getItem("next_date");
            this.fetchStatisticsData(timeframe, dateFrom, dateTo);
            this.fetchTotalsData(timeframe, dateFrom, dateTo);
        } else {
            this.fetchStatisticsData(timeframe);
            this.fetchTotalsData(timeframe);
        }
    };

    processNotesTooltip = (notes = []) => {
        if (!Array.isArray(notes) || notes.length === 0) return null;

        const { call_at_timezone } = this.props.state.business;

        const listItems = notes.slice(0,3).map(note => {
            const createdAt = DateFormatter.formatDate(note.created_at, call_at_timezone);
            const createdBy = escapeHtml(note.creator.full_name || __("Unknown"));
            const noteText = escapeHtml(note.note || "");

            return `
            <li>
                <strong>${__("Note")}:</strong> ${noteText}<br/>
                <small>${__("Created by")}: ${createdBy} ${__("on")}: ${createdAt}</small>
            </li>
        `;
        }).join("");

        return `
        <ul class="pl-2 m-0">
            ${listItems}
        </ul>
    `;
    };

    processRemindersTooltip = (reminders = []) => {
        if (!Array.isArray(reminders) || reminders.length === 0) return null;

        let html = "<ul style='padding-left: 1em; margin: 0;'>";

        reminders.slice(0,3).forEach(reminder => {
            const remindAt = DateFormatter.formatDate(reminder.remind_at, this.props.state.business.call_at_timezone);
            const timeUntil = DateFormatter.getTimeUntil(reminder.remind_at);
            const title = escapeHtml(reminder.title || __("Untitled"));
            const note = escapeHtml(reminder.note || "-");

            html += `<li class="mb-3">
            <strong>${__("Reminder")}:</strong> ${title}
            <small>${__("Note")}: ${note}</small>
            <small>${__("At")}: ${remindAt} (${timeUntil})</small>`;

            if (Array.isArray(reminder.recipients) && reminder.recipients.length > 0) {
                html += `<small>${__("Recipients")}:<ul style="padding-left: 1em; margin: 0;">`;
                reminder.recipients.forEach(recipient => {
                    const name = escapeHtml(recipient.full_name || "?");
                    const methods = [
                        recipient.via_email ? __("Email") : null,
                        recipient.via_text ? __("Text") : null,
                        recipient.via_push ? __("Push") : null,
                    ].filter(Boolean).join(", ");

                    html += `<li>${name}${methods ? ` — ${methods}` : ""}</li>`;
                });
                html += "</ul></small>";
            }

            html += `</li>`;
        });

        html += "</ul>";

        return html;
    };

    reloadBusinessNotesAndReminders = async (businessId) => {
        try {
            const [notesRes, remindersRes] = await Promise.all([
                axios.get(`/business-notes?business_id=${businessId}&per_page=3`),
                axios.get(`/reminders?business_id=${businessId}&per_page=3&onlyFuture=true`),
            ]);

            const notes = notesRes.data?.data ?? [];
            const reminders = remindersRes.data?.data ?? [];

            this.setState(prev => ({
                apiStatistics: prev.apiStatistics.map(row => {
                    if (row.businessId === businessId) {
                        return {
                            ...row,
                            notes,
                            reminders,
                        };
                    }
                    return row;
                }),
            }), () => ReactTooltip.rebuild());
        } catch (error) {
            console.error("Failed to reload notes or reminders:", error);
        }
    };

    getColumns = () => {
        const feedbackStatuses = window.feedbackStatus;
        const timeframe = this.props.state.timeframe;

        const generateFeedbacksURL = (base_url, feedbackStatus, chats = null) => {
            let others = chats ? chats : this.props.other;
            let url = `${base_url}/timeframe/${timeframe}/status/${feedbackStatus}/other/${others}`;
            if (feedbackStatuses.RatingsOnly.value === feedbackStatus) {
                url += `?filterRating=1,2,3,4,5`;
            }
            return { feedbacksUrl: url };
        };

        const calcAIScore = (value) => {
            let score;
            if (value !== undefined) {
                score = value < 50
                    ? "text-danger"
                    : value < 65
                        ? "text-warning"
                        : "text-success";
            }
            return score;
        };

        return [
            {
                width: "4%",
                key: "notes",
                label: __("Action"),
                sortable: true,
                searchable: false,
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{__("N/A")}</strong>;

                    const notes = row.notes || [];
                    const reminders = row.reminders || [];

                    const notesHtml = this.processNotesTooltip(notes);
                    const remindersHtml = this.processRemindersTooltip(reminders);

                    const tooltipId = `notes-${row.businessId}`;
                    const hasIcons = notesHtml || remindersHtml;

                    return (
                        <div
                            className="tooltip-text w-100 h-100 d-flex align-items-center justify-content-center"
                            style={{ cursor: "pointer", gap: "6px" }}
                        >
                            <a
                                href={`/view-as-business/login/${row.businessId}/dashboard`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                data-tip={__("Log in as business")}
                                data-for={`${tooltipId}-login`}
                                style={{ display: "inline-flex", alignItems: "center" }}
                            >
                                <svg className="login-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style={{ width: "14px", height: "14px", fill: "#007bff" }}>
                                    <path d="M416 448h-84c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h84c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32h-84c-6.6 0-12-5.4-12-12V76c0-6.6 5.4-12 12-12h84c53 0 96 43 96 96v192c0 53-43 96-96 96zm-47-201L177 55c-9.4-9.4-24.6-9.4-33.9 0l-22.7 22.7c-9.4 9.4-9.4 24.6 0 33.9L239.8 232H12c-13.3 0-24 10.7-24 24v32c0 13.3 10.7 24 24 24h227.8L120.4 431.4c-9.4 9.4-9.4 24.6 0 33.9l22.7 22.7c9.4 9.4 24.6 9.4 33.9 0l192-192c9.4-9.4 9.4-24.6 0-33.9z" />
                                </svg>
                            </a>
                            <ReactTooltip id={`${tooltipId}-login`} effect="solid" place="right" />

                            <div
                                onClick={(e) => { e.stopPropagation(); this.handleNotesClick(row); }}
                                style={{ display: "inline-flex", alignItems: "center" }}
                            >
                                {notesHtml && (
                                    <>
                                        <div data-tip={notesHtml}
                                             data-tooltip-html={notesHtml}
                                             data-for={`${tooltipId}-notes`}>
                                            <svg className="notes-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                                <path d="M256 32C114.6 32 0 125.1 0 240c0 45.1 17.7 86.8 47.8 120.1-1.9 24.5-11.4 46.3-21.4 62.9-5.6 9.2-11.1 16.6-15.2 21.6-2.1 2.6-3.8 4.5-4.9 5.7-.6.7-1 .1-1.3 1.4l-.3.3c-4.5 4.6-5.9 11.4-3.4 17.4 2.5 6 8.3 9.9 14.8 9.9 28.7 0 57.6-8.9 81.6-20.5 23-10 42.4-21.9 54.3-30.6 31.8 11.5 67 17.9 104.1 17.9 141.4 0 256-93.1 256-208S397.4 32 256 32z" />
                                            </svg>
                                        </div>
                                        <ReactTooltip id={`${tooltipId}-notes`} html={true} className="tooltip-content" effect="solid" place="right" />
                                    </>
                                )}

                                {remindersHtml && (
                                    <>
                                        <div data-tip={remindersHtml}
                                             data-tooltip-html={remindersHtml}
                                             data-for={`${tooltipId}-reminders`}>
                                            <svg className="reminders-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                                <path d="M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64zm0 336c-79.5 0-144-64.5-144-144S176.5 112 256 112s144 64.5 144 144-64.5 144-144 144zm16-224h-32v96h80v-32h-48z"/>
                                            </svg>
                                        </div>
                                        <ReactTooltip id={`${tooltipId}-reminders`} html={true} className="tooltip-content" effect="solid" place="right" />
                                    </>
                                )}

                                {!hasIcons && (
                                    <>
                                        <div data-tip={__("Add note or reminder")}
                                             data-tooltip-html={__("Add note or reminder")}
                                             data-for={`${tooltipId}-plus`}>
                                            <svg className="plus-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                                <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm112 276h-88v88c0 13.3-10.7 24-24 24s-24-10.7-24-24v-88h-88c-13.3 0-24-10.7-24-24s10.7-24 24-24h88v-88c0-13.3 10.7-24 24-24s24 10.7 24 24v88h88c13.3 0 24 10.7 24 24s-10.7 24-24 24z" />
                                            </svg>
                                        </div>
                                        <ReactTooltip id={`${tooltipId}-plus`} effect="solid" place="right" />
                                    </>
                                )}
                            </div>
                        </div>
                    );
                }
            },
            {
                width: "14%",
                key: "businessName",
                label: __("Location"),
                sortable: true,
                defaultSort: "asc",
                searchable: true,
                sortValue: row => row.businessName || "",
                render: (value, row) => row.isTotalRow
                    ? <strong>{row.totalBusinesses} {__("Locations")}</strong>
                    : <a href={`/business/${row.businessId}/company-profile`} target="_blank">{value}</a>
            },
            {
                width: "10%",
                key: "businessGroupName",
                label: __("Business Group"),
                sortable: true,
                searchable: true,
                sortValue: row => row.businessGroupName || "",
                render: (value, row) => value
                    ? <a href={`/admin/business-group/${row.businessGroupId}/edit`} target="_blank">{value}</a>
                    : <span>{__("N/A")}</span>
            },
            {
                width: "6%",
                key: "businessStatus",
                label: "Status",
                sortable: true,
                render: value => window.businessStatus?.[value]?.label || <span>{__("N/A")}</span>
            },
            {
                width: "8%",
                key: "industryName",
                label: __("Industry"),
                sortable: true,
                render: (value, row) => row.isTotalRow
                    ? <strong>{__("N/A")}</strong>
                    : value
            },
            ...["All", "Open", "Resolved", "RatingsOnly"].map(statusKey => ({
                width: "4%",
                key: statusKey === "All" ? "totalFeedbacksWithoutRatingsOnly" : (statusKey.charAt(0).toLowerCase() + statusKey.slice(1)),
                label: statusKey === "All" ? "Feedbacks" : feedbackStatuses[statusKey]?.label || statusKey,
                sortable: true,
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{value}</strong>;
                    const url = generateFeedbacksURL(`/business/${row.businessId}/feedbacks`, feedbackStatuses[statusKey].value);
                    return value > 0 ? <a href={url.feedbacksUrl} target="_blank">{value}</a> : 0;
                }
            })),
            {
                width: "6%",
                key: "medianResponseTime.formatted",
                label: __("Median Resp Time"),
                sortable: true,
                sortValue: row => row.medianResponseTime?.seconds || "",
                render: value => value || __("No data")
            },
            {
                width: "6%",
                key: "averageRating",
                label: __("Ratings"),
                sortable: true,
                sortValue: row => row.averageRating || "",
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{parseFloat(value).toFixed(2)}</strong>;
                    return value > 0 ? (
                        <div>
                            <span className="mr-1 d-block">{parseFloat(value).toFixed(2)}</span>
                            <span className="font-10px">{getStarsJSX(value, "OTF", true)}</span>
                        </div>
                    ) : __("No data");
                }
            },
            {
                width: "6%",
                key: "businessCreatedAt",
                label: __("Launch Date"),
                sortable: true,
                sortValue: row => row.businessCreatedAt || "",
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{__("N/A")}</strong>;
                    return value ? new Date(value).toLocaleDateString() : __("No data");
                }
            },
            {
                width: "6%",
                key: "businessMonthlySubscriptionFee",
                label: __("Monthly Fee"),
                sortable: true,
                sortValue: row => row.businessMonthlySubscriptionFee || "",
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{__("N/A")}</strong>;
                    return value ? `$${parseFloat(value).toFixed(2)}` : __("No data");
                }
            },
            {
                width: "6%",
                key: "businessWebWidget",
                label: __("Web to Text"),
                sortable: true,
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{__("N/A")}</strong>;
                    return value ? __("Yes") : __("No");
                }
            },
            {
                width: "6%",
                key: "totalQrCodes",
                label: __("Total QR Codes"),
                sortable: true,
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{__("N/A")}</strong>;
                    return value > 0 ? value : 0;
                }
            },
            {
                width: "6%",
                key: "businessAssociatedEmployee",
                label: __("Assoc. Emp."),
                sortable: true,
                render: (value, row) => {
                    if (row.isTotalRow) return <strong>{__("N/A")}</strong>;
                    return value ? value : "-";
                }
            }
        ];
    };

    render() {
        const { apiStatistics, apiDataTotals, statsLoading, totalsLoading, statsError, totalsError, onlyManagedServices } = this.state;
        const isLoading = statsLoading || totalsLoading;
        const error = statsError || totalsError;

        const filteredStatistics = onlyManagedServices
            ? apiStatistics.filter(row => row.businessManagedServices)
            : apiStatistics;

        return (
            <div className="container-fluid dashboard-container">
                <Loading location="page" />
                <div className="row dashboard-stats sortable-table">
                    <DashboardActionBar onTimeframeChange={this.handleTimeframeChange} hideExport={true} />

                    <TableOverlay loading={isLoading} error={error} onRetry={this.retryFetch}>
                        <SmartTableGrid
                            data={filteredStatistics}
                            totalsRow={apiDataTotals}
                            columns={this.getColumns()}
                            enableSearch={true}
                            enablePagination={true}
                            enableItemsPerPage={true}
                            enableFilters={{
                                industryName: ["All", ...Array.from(new Set(apiStatistics.map(item => item.industryName).filter(Boolean)))]
                            }}
                            customControls={
                                <div className="mr-4">
                                    <label className="mb-0" style={{ cursor: "pointer", fontWeight: "normal" }}>
                                        <input
                                            type="checkbox"
                                            checked={onlyManagedServices}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                window.localStorage.setItem("only_managed_services", checked ? "1" : "0");
                                                this.setState({ onlyManagedServices: checked });
                                            }}
                                            className="mr-1"
                                        />
                                        {__("Only Show Businesses with Managed Services")}
                                    </label>
                                </div>
                            }
                        />
                    </TableOverlay>
                </div>
                {this.state.showNoteReminderModal && (
                    <BusinessNoteReminderModal
                        isOpen={this.state.showNoteReminderModal}
                        onClose={() => this.setState({ showNoteReminderModal: false })}
                        onSaved={() => {
                            const businessId = this.state.selectedBusinessRow?.businessId;

                            this.setState({ showNoteReminderModal: false }, () => {
                                if (businessId) {
                                    this.reloadBusinessNotesAndReminders(businessId);
                                }
                            });
                        }}
                        businessId={this.state.selectedBusinessRow?.businessId}
                        businessName={this.state.selectedBusinessRow?.businessName}
                        currentUserHash={this.props.state.current_user}
                        currentUserId={this.props.user_id}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    state,
    user_id: window.user_id,
});

export default connect(mapStateToProps)(AllDashboard);
